// store/thunks/branchAppointmentConfigThunks.js
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import branchAppointmentConfigService from "../../services/branchAppointmentConfigService";

// ========== APPOINTMENT CONFIGURATION THUNKS ==========

export const getAppointmentConfigThunk = createAsyncThunk(
  "branchAppointmentConfig/getAppointmentConfig",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.getAppointmentConfig(branchCode);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAppointmentConfigThunk = createAsyncThunk(
  "branchAppointmentConfig/updateAppointmentConfig",
  async ({ branchCode, configData }, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.updateAppointmentConfig(
          branchCode,
          configData
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== DAY BUFFERS THUNKS ==========

export const getDayBuffersThunk = createAsyncThunk(
  "branchAppointmentConfig/getDayBuffers",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getDayBuffers(
        branchCode
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateDayBuffersThunk = createAsyncThunk(
  "branchAppointmentConfig/updateDayBuffers",
  async ({ branchCode, dayBuffers }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.updateDayBuffers(
        branchCode,
        dayBuffers
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkUpdateDayBuffersThunk = createAsyncThunk(
  "branchAppointmentConfig/bulkUpdateDayBuffers",
  async ({ branchCodes, dayBuffers }, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.bulkUpdateDayBuffers(
          branchCodes,
          dayBuffers
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== BLOCKED PERIODS THUNKS ==========

export const getBlockedPeriodsThunk = createAsyncThunk(
  "branchAppointmentConfig/getBlockedPeriods",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getBlockedPeriods(
        branchCode
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const addBlockedPeriodThunk = createAsyncThunk(
  "branchAppointmentConfig/addBlockedPeriod",
  async ({ branchCode, blockedPeriod }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.addBlockedPeriod(
        branchCode,
        blockedPeriod
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateBlockedPeriodThunk = createAsyncThunk(
  "branchAppointmentConfig/updateBlockedPeriod",
  async ({ branchCode, blockIndex, updates }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.updateBlockedPeriod(
        branchCode,
        blockIndex,
        updates
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteBlockedPeriodThunk = createAsyncThunk(
  "branchAppointmentConfig/deleteBlockedPeriod",
  async ({ branchCode, blockIndex }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.deleteBlockedPeriod(
        branchCode,
        blockIndex
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const toggleBlockedPeriodThunk = createAsyncThunk(
  "branchAppointmentConfig/toggleBlockedPeriod",
  async ({ branchCode, blockIndex }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.toggleBlockedPeriod(
        branchCode,
        blockIndex
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkAddBlockedPeriodsThunk = createAsyncThunk(
  "branchAppointmentConfig/bulkAddBlockedPeriods",
  async ({ branchCodes, blockedPeriod }, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.bulkAddBlockedPeriods(
          branchCodes,
          blockedPeriod
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const copyBlockedPeriodsThunk = createAsyncThunk(
  "branchAppointmentConfig/copyBlockedPeriods",
  async ({ sourceBranchCode, targetBranchCodes }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.copyBlockedPeriods(
        sourceBranchCode,
        targetBranchCodes
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const compareBlockedPeriodsThunk = createAsyncThunk(
  "branchAppointmentConfig/compareBlockedPeriods",
  async (branchCodes = null, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.compareBlockedPeriods(branchCodes);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== WORKING HOURS & SCHEDULE THUNKS ==========

export const checkIsOpenThunk = createAsyncThunk(
  "branchAppointmentConfig/checkIsOpen",
  async ({ branchCode, dateTime }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.checkIsOpen(
        branchCode,
        dateTime
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getWorkingHoursThunk = createAsyncThunk(
  "branchAppointmentConfig/getWorkingHours",
  async ({ branchCode, date }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getWorkingHours(
        branchCode,
        date
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getTodayScheduleThunk = createAsyncThunk(
  "branchAppointmentConfig/getTodaySchedule",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getTodaySchedule(
        branchCode
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getNextOpeningThunk = createAsyncThunk(
  "branchAppointmentConfig/getNextOpening",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getNextOpening(
        branchCode
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== HOLIDAYS THUNKS ==========

export const checkIsHolidayThunk = createAsyncThunk(
  "branchAppointmentConfig/checkIsHoliday",
  async ({ branchCode, date }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.checkIsHoliday(
        branchCode,
        date
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getHolidaysThunk = createAsyncThunk(
  "branchAppointmentConfig/getHolidays",
  async ({ branchCode, startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getHolidays(
        branchCode,
        startDate,
        endDate
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== CAPACITY MANAGEMENT THUNKS ==========

export const checkCapacityThunk = createAsyncThunk(
  "branchAppointmentConfig/checkCapacity",
  async ({ branchCode, date }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.checkCapacity(
        branchCode,
        date
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAppointmentStatsThunk = createAsyncThunk(
  "branchAppointmentConfig/updateAppointmentStats",
  async ({ branchCode, statsData }, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.updateAppointmentStats(
          branchCode,
          statsData
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== EXCHANGE CHARGES THUNKS ==========

export const getExchangeChargesThunk = createAsyncThunk(
  "branchAppointmentConfig/getExchangeCharges",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getExchangeCharges(
        branchCode
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== NEARBY BRANCHES THUNKS ==========

export const getNearbyBranchesThunk = createAsyncThunk(
  "branchAppointmentConfig/getNearbyBranches",
  async ({ latitude, longitude, radiusKm }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getNearbyBranches(
        latitude,
        longitude,
        radiusKm
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== APPOINTMENT TYPES THUNKS ==========

export const getAppointmentTypesThunk = createAsyncThunk(
  "branchAppointmentConfig/getAppointmentTypes",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getAppointmentTypes(
        branchCode
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAppointmentTypeThunk = createAsyncThunk(
  "branchAppointmentConfig/updateAppointmentType",
  async ({ branchCode, typeConfig }, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.updateAppointmentType(
          branchCode,
          typeConfig
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== AUTO-APPROVAL SETTINGS THUNKS ==========

export const getAutoApprovalSettingsThunk = createAsyncThunk(
  "branchAppointmentConfig/getAutoApprovalSettings",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.getAutoApprovalSettings(
          branchCode
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAutoApprovalSettingsThunk = createAsyncThunk(
  "branchAppointmentConfig/updateAutoApprovalSettings",
  async ({ branchCode, settings }, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.updateAutoApprovalSettings(
          branchCode,
          settings
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== BOOKING WINDOW THUNKS ==========

export const getBookingWindowThunk = createAsyncThunk(
  "branchAppointmentConfig/getBookingWindow",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getBookingWindow(
        branchCode
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateBookingWindowThunk = createAsyncThunk(
  "branchAppointmentConfig/updateBookingWindow",
  async ({ branchCode, windowConfig }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.updateBookingWindow(
        branchCode,
        windowConfig
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== NOTIFICATION SETTINGS THUNKS ==========

export const getNotificationSettingsThunk = createAsyncThunk(
  "branchAppointmentConfig/getNotificationSettings",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.getNotificationSettings(
          branchCode
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateNotificationSettingsThunk = createAsyncThunk(
  "branchAppointmentConfig/updateNotificationSettings",
  async ({ branchCode, settings }, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.updateNotificationSettings(
          branchCode,
          settings
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== CANCELLATION POLICY THUNKS ==========

export const getCancellationPolicyThunk = createAsyncThunk(
  "branchAppointmentConfig/getCancellationPolicy",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.getCancellationPolicy(branchCode);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateCancellationPolicyThunk = createAsyncThunk(
  "branchAppointmentConfig/updateCancellationPolicy",
  async ({ branchCode, policy }, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.updateCancellationPolicy(
          branchCode,
          policy
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== SCHEDULING RULES THUNKS ==========

export const getSchedulingRulesThunk = createAsyncThunk(
  "branchAppointmentConfig/getSchedulingRules",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getSchedulingRules(
        branchCode
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateSchedulingRulesThunk = createAsyncThunk(
  "branchAppointmentConfig/updateSchedulingRules",
  async ({ branchCode, schedulingRules }, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.updateSchedulingRules(
          branchCode,
          schedulingRules
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== AVAILABLE SLOTS THUNKS ==========

export const getAvailableSlotsThunk = createAsyncThunk(
  "branchAppointmentConfig/getAvailableSlots",
  async ({ branchCode, date, appointmentType }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getAvailableSlots(
        branchCode,
        date,
        appointmentType
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getWeeklySlotsThunk = createAsyncThunk(
  "branchAppointmentConfig/getWeeklySlots",
  async ({ branchCode, startDate }, { rejectWithValue }) => {
    try {
      const response = await branchAppointmentConfigService.getWeeklySlots(
        branchCode,
        startDate
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const checkSlotAvailabilityThunk = createAsyncThunk(
  "branchAppointmentConfig/checkSlotAvailability",
  async ({ branchCode, date, timeSlot }, { rejectWithValue }) => {
    try {
      const response =
        await branchAppointmentConfigService.checkSlotAvailability(
          branchCode,
          date,
          timeSlot
        );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== ACTION CREATORS ==========

export const clearAppointmentConfigError = createAction(
  "branchAppointmentConfig/clearError"
);

export const resetAppointmentConfig = createAction(
  "branchAppointmentConfig/resetConfig"
);

export const setSelectedBranchCode = createAction(
  "branchAppointmentConfig/setSelectedBranchCode"
);

export const setConfigEditMode = createAction(
  "branchAppointmentConfig/setConfigEditMode"
);
